// ==================== LEAD GEN RUN CONTEXT ====================
// Single source of truth for which pipeline runs are active right now, across
// all Lead Generation pages. Several campaigns can run at once (e.g. a one-off
// next to a continuous schedule), so state is kept per run id:
//   1. Restored from the server on mount (survives page refresh) via
//      GET /customer/lead-gen/active-run, and
//   2. Kept live via real-time run.started / run.progress / run.completed /
//      run.failed WebSocket events, each routed to its own run.
//
// Consumers:
//   - LeadGenRunStatus      → live banner (one run, or "N campaigns running")
//   - LeadDiscoveryPage     → live stats on every running campaign card
//   - LeadDiscoveryCampaignPage → live progress for that campaign

import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLeadGenEvents } from '../../../hooks/useLeadGenEvents';
import { leadGenService } from '../../../services/lead-gen.service';

export interface RunState {
  active: boolean;
  done: boolean;
  failed?: boolean;
  errorMessage?: string;
  /** pipeline_run_id — lets a campaign page know the live run is *this* campaign. */
  runId?: string;
  stage: string;
  location?: string;
  industries?: string[];
  discovered?: number;
  passed?: number;
  evaluated?: number;
  qualified?: number;
  drafted?: number;
  sent?: number;
  totalDrafts?: number;
  /** When this run last changed (ms) — orders runs newest first. */
  updatedAt?: number;
}

interface LeadGenRunContextValue {
  /** The most recently updated run (drives the single-run banner). */
  run: RunState | null;
  /** Every tracked run by run id (active, and recently finished for a few seconds). */
  runs: Record<string, RunState>;
  /** Runs still in progress, newest first. */
  activeRuns: RunState[];
  /** Live state for one campaign, or null if it isn't running. */
  getRun: (runId?: string | null) => RunState | null;
  /** True while any run is in progress. */
  isRunning: boolean;
  /** Optimistically mark a run as started (instant feedback on button click). */
  markStarted: (partial?: Partial<RunState>) => void;
}

const LeadGenRunContext = createContext<LeadGenRunContextValue>({
  run: null,
  runs: {},
  activeRuns: [],
  getRun: () => null,
  isRunning: false,
  markStarted: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useLeadGenRun = () => useContext(LeadGenRunContext);

/** Key for a run started optimistically before its id is known. */
const PENDING = '__pending__';
const DONE_VISIBLE_MS = 9000;
const FAILED_VISIBLE_MS = 12000;

export function LeadGenRunProvider({ children }: { children: ReactNode }) {
  const [runs, setRuns] = useState<Record<string, RunState>>({});
  const [lastKey, setLastKey] = useState<string | null>(null);
  const removeTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const scheduleRemoval = (key: string, ms: number) => {
    clearTimeout(removeTimers.current[key]);
    removeTimers.current[key] = setTimeout(() => {
      delete removeTimers.current[key];
      setRuns((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, ms);
  };

  const upsert = (key: string, update: (prev: RunState | undefined) => RunState) => {
    clearTimeout(removeTimers.current[key]);
    setRuns((prev) => {
      const next = { ...prev, [key]: { ...update(prev[key]), updatedAt: Date.now() } };
      // The real run id arrived: drop the optimistic placeholder.
      if (key !== PENDING) delete next[PENDING];
      return next;
    });
    setLastKey(key);
  };

  // ---- Restore active runs on mount / refresh (server-backed) --------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await leadGenService.getActiveRun();
        if (cancelled || !res.success || !res.data?.active) return;
        const list = res.data.runs?.length ? res.data.runs : res.data.run ? [res.data.run] : [];
        const restored: Record<string, RunState> = {};
        for (const raw of list) {
          const r = raw as Record<string, unknown> & typeof raw;
          const n = (k: string) => (typeof r[k] === 'number' ? (r[k] as number) : undefined);
          restored[r.run_id] = {
            active: true,
            done: false,
            runId: r.run_id,
            stage: r.stage || 'discovery',
            location: r.location ?? undefined,
            industries: r.industries ?? undefined,
            discovered: n('discovered'),
            passed: n('passed'),
            evaluated: n('evaluated'),
            qualified: n('qualified'),
            drafted: n('drafted'),
            sent: n('sent'),
            updatedAt: typeof r.started_at === 'number' ? r.started_at * 1000 : Date.now(),
          };
        }
        // Events that arrived while loading are newer — keep them.
        setRuns((prev) => ({ ...restored, ...prev }));
        setLastKey((prev) => prev ?? list[0]?.run_id ?? null);
      } catch {
        /* best-effort — no active run / offline */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timers = removeTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // ---- Live updates via WebSocket, routed by run id --------------------------
  const onEvent = useCallback((e: { event: string; data?: Record<string, unknown> }) => {
    const d = (e.data ?? {}) as Record<string, unknown>;
    const num = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : undefined);
    const key = typeof d.run_id === 'string' ? d.run_id : PENDING;

    if (e.event === 'run.started') {
      upsert(key, () => ({
        active: true,
        done: false,
        runId: key === PENDING ? undefined : key,
        stage: String(d.stage ?? 'discovery'),
        location: d.location as string | undefined,
        industries: (d.industries as string[] | undefined) ?? undefined,
      }));
    } else if (e.event === 'run.progress') {
      upsert(key, (prev) => ({
        active: true,
        done: false,
        runId: key === PENDING ? prev?.runId : key,
        stage: String(d.stage ?? prev?.stage ?? 'discovery'),
        location: (d.location as string) ?? prev?.location,
        industries: (d.industries as string[]) ?? prev?.industries,
        discovered: num('discovered') ?? prev?.discovered,
        passed: num('passed') ?? prev?.passed,
        evaluated: num('evaluated') ?? prev?.evaluated,
        qualified: num('qualified') ?? prev?.qualified,
        drafted: num('drafted') ?? prev?.drafted,
        sent: num('sent') ?? prev?.sent,
      }));
    } else if (e.event === 'run.completed') {
      upsert(key, (prev) => ({
        ...prev,
        active: false,
        done: true,
        runId: key === PENDING ? prev?.runId : key,
        stage: 'done',
        discovered: num('total_discovered') ?? prev?.discovered,
        totalDrafts: num('total_outreach_drafts'),
        sent: num('total_auto_sent') ?? prev?.sent,
      }));
      scheduleRemoval(key, DONE_VISIBLE_MS);
    } else if (e.event === 'run.failed') {
      upsert(key, (prev) => ({
        ...prev,
        active: false,
        done: true,
        failed: true,
        runId: key === PENDING ? prev?.runId : key,
        stage: 'failed',
        errorMessage: typeof d.error === 'string' ? d.error : undefined,
      }));
      scheduleRemoval(key, FAILED_VISIBLE_MS);
    }
    // upsert / scheduleRemoval only touch refs and state setters.
  }, []);

  useLeadGenEvents({
    onEvent,
    events: ['run.started', 'run.progress', 'run.completed', 'run.failed'],
  });

  const markStarted = useCallback((partial?: Partial<RunState>) => {
    const key = partial?.runId ?? PENDING;
    upsert(key, (prev) => ({ ...prev, active: true, done: false, stage: 'discovery', ...partial }));
  }, []);

  const value = useMemo<LeadGenRunContextValue>(() => {
    const activeRuns = Object.values(runs)
      .filter((r) => r.active && !r.done && !r.failed)
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
    const run = (lastKey && runs[lastKey]) || activeRuns[0] || null;
    return {
      run,
      runs,
      activeRuns,
      getRun: (runId) => (runId ? runs[runId] ?? null : null),
      isRunning: activeRuns.length > 0,
      markStarted,
    };
  }, [runs, lastKey, markStarted]);

  return <LeadGenRunContext.Provider value={value}>{children}</LeadGenRunContext.Provider>;
}
